// File: src/components/layout/Header.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Header.module.css';
import FullPageMenu from './FullPageMenu';
import SearchModal from './SearchModal';
import { SITE_NAME, ASSETS_BASE_URL } from '../../config/constants';
import { cn } from '../../utils/helpers';

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const isHome = location.pathname === '/';
  const headerTransparent = !isScrolled && isHome && !isMenuOpen;

  return (
    <>
      <header
        className={cn(
          styles.header,
          isScrolled && styles.scrolled,
          headerTransparent && styles.transparent,
          isMenuOpen && styles.menuActive
        )}
      >
        <div className={cn('site-container', styles.inner)}>
          {/* Logo */}
          <Link to="/" className={styles.logo} aria-label={SITE_NAME}>
            <img 
              src={`${ASSETS_BASE_URL}/greenscience-new-full.svg`}
              alt="GreenScience Technology"
              className={styles.logoImg}
              onError={(e) => {
                console.error('Logo failed to load');
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span style="color: var(--color-primary); font-weight: 600; font-size: 18px;">GreenScience Technology</span>';
              }}
            />
          </Link>

          {/* Actions */}
          <div className={styles.actions}>
            {/* Search */}
            <button
              className={styles.iconBtn}
              onClick={() => setIsSearchOpen(true)}
              aria-label="Search"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>

            {/* Hamburger */}
            <button
              className={cn(styles.menuBtn, isMenuOpen && styles.menuBtnOpen)}
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span className={styles.menuLabel}>
                {isMenuOpen ? 'Close' : 'Menu'}
              </span>
              <span className={styles.menuIcon}>
                <span className={styles.menuBar} />
                <span className={styles.menuBar} />
              </span>
            </button>

            {/* Let's Talk CTA */}
            <Link to="/contact" className={styles.ctaBtn}>
              Let's Talk
            </Link>
          </div>
        </div>
      </header>

      <FullPageMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
}

export default Header;